import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapActions, mapGetters, mapState } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { KForm, KFormSelect, KFormInput, KFormAddress, KFormPhone, KFormCheckbox, KFormDatepicker } from '@kwixeo/form-vue'
import {
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormRadioGroup,
  BFormRadio,
  BFormRow,
  BTabs,
  BTab,
  BListGroup,
  BListGroupItem
} from 'bootstrap-vue'
import { CustomFieldsForm } from '@kwixeo/custom-fields-vue'
import { CivilitySelect, Spinner, TagSelect, emitter, TeamUserSelect } from '@kwixeo/interface-vue'
import axios, { AxiosResponse } from 'axios'
import CustomerSaleParams from '../CustomerSaleParams/CustomerSaleParams.vue'
import CustomerSelect from '../CustomerSelect/CustomerSelect.vue'
import ContactSelect from '../ContactSelect/ContactSelect.vue'

@Component({
  computed: {
    ...mapState('globalDatas', ['countries']),
    ...mapGetters('user', ['team', 'teamUser', 'teamConfigs']),
    ...mapState('customers', ['customerTypes', 'origins', 'activities']),
    ...mapGetters('customers', ['defaultOrigin', 'defaultActivity']),
    ...mapGetters('subscriptions', ['hasModule'])
  },
  methods: {
    ...mapActions('globalDatas', ['loadCountries', 'loadTeamUsers']),
    ...mapActions('customers', ['loadCustomerTypes', 'loadActivities', 'loadOrigins'])
  },
  components: {
    CustomerSelect,
    ContactSelect,
    CustomFieldsForm,
    CustomerSaleParams,
    CivilitySelect, TagSelect, Spinner, TeamUserSelect,
    KForm, KFormSelect, KFormInput, KFormAddress, KFormPhone, KFormCheckbox, KFormDatepicker,
    BRow, BCol, BFormGroup, BFormRadioGroup, BFormRadio, BFormRow, BTabs, BTab, BListGroup, BListGroupItem, BButton,
    FontAwesomeIcon
  }
})
export default class CustomerForm extends Vue {
  loadCountries!: (force?: boolean) => void
  loadCustomerTypes!: (force?: boolean) => void
  loadActivities!: (force?: boolean) => void
  loadOrigins!: (force?: boolean) => void
  loadTeamUsers!: (force?: boolean) => void
  team!: KwixeoFramework.Team
  teamUser!: KwixeoFramework.TeamUser
  teamConfigs!: KwixeoInterface.GenericObject
  customerTypes!: KwixeoCustomers.CustomerTypes
  activities!: KwixeoCustomers.Activities
  defaultActivity!: KwixeoCustomers.Activity
  origins!: KwixeoCustomers.Origins
  defaultOrigin!: KwixeoCustomers.Origin
  hasModule!: (key: string) => boolean

  @Prop({ default: null }) value!: KwixeoCustomers.Customer
  @Prop({ required: true }) type!: KwixeoCustomers.Types
  @Prop({ default: true }) withRedirection!: boolean|undefined

  private loading = true
  private localCustomer: KwixeoCustomers.CustomerForm = {}
  private deliveryUseInvoiceAddress = true
  private showSalesPreferences = false

  private searchCompanyTimeout: number|null = null
  private searchCompanyResults: KwixeoCustomers.SiretApiResults|null = null

  get initialCountry (): string {
    if (this.localCustomer && this.localCustomer.address_invoice?.country) {
      return this.localCustomer.address_invoice?.country.code
    }
    return this.team?.country?.code ?? 'fr'
  }

  get computedType (): Partial<KwixeoCustomers.CustomerType> {
    if (this.localCustomer.type) {
      return this.localCustomer.type
    }
    if (this.customerTypes && this.customerTypes.length > 0) {
      const defaultType = this.customerTypes.find(type => type.use_default)
      if (defaultType) {
        return defaultType
      }
    return this.customerTypes[0]
    }
    return {}
  }

  get apiFormUrl (): string {
    let url = '/customer'
    if (this.localCustomer.id) {
      url += '/' + this.localCustomer.id
    }
    return url
  }

  async mounted (): Promise<void> {
    await this.loadCustomerTypes()
    await this.loadCountries()
    await this.loadTeamUsers()
    await this.loadActivities()
    await this.loadOrigins()
    this.setLocalCustomer(this.value)
    this.loading = false
  }

  beforeDestroy(): void {
    window.document.removeEventListener('click', this.searchCompanyClose)
  }

  private setLocalCustomer (customer: KwixeoCustomers.CustomerForm) {
    if (!customer) {
      this.localCustomer = {
        address_invoice: {
          country: this.team.country
        },
        team_user: this.teamUser,
        newsletter_inscription: this.teamConfigs.automatic_newsletter_subscription,
        type: this.computedType,
        activity: this.defaultActivity,
        origin: this.defaultOrigin
      }
      return;
    }

    customer = JSON.parse(JSON.stringify(customer))

    const delivery = customer.address_delivery
    const invoice = customer.address_invoice
    if (delivery && (!invoice || invoice.id !== delivery.id)) {
      this.deliveryUseInvoiceAddress = false
    }

    this.localCustomer = customer
  }

  private async submitComplete (response: AxiosResponse): Promise<void> {
    const customer: KwixeoCustomers.Customer = response.data
    if (this.value && this.value.id) {
      emitter.emit('customerUpdated', { old: this.value, new: customer })
    } else {
      emitter.emit('customerCreated', customer)

      if (this.withRedirection) {
        await this.$router.push({
          name: 'contacts-customer-item',
          params: {
            id: String(customer.id),
            type: this.type
          }
        })
      }
    }
  }

  private searchCompany (): void {
    if (!this.teamConfigs.fetch_customer_sirene_api || !this.$kwCustomers.sireneApitoken || (this.localCustomer.address_invoice?.country && !['FR', 'GP', 'RE', 'MQ'].includes(this.localCustomer.address_invoice?.country?.code ?? ''))) {
      return
    }

    if (this.searchCompanyTimeout) {
      clearTimeout(this.searchCompanyTimeout)
    }

    if (!this.localCustomer.company_name) {
      this.searchCompanyResults = null
      return
    }

    this.searchCompanyTimeout = setTimeout(this.processSearchCompany, 500)
  }

  private async processSearchCompany (): Promise<void> {
    const name = this.localCustomer.company_name
    if ((name?.length ?? 0) < 3) {
      this.searchCompanyResults = null
      return
    }

    await axios.get(this.$kwCustomers.sireneApiUrl + '/siret', {
      headers: {
        Authorization: 'Bearer ' + this.$kwCustomers.sireneApitoken
      },
      withCredentials: true,
      params: {
        name,
        per_page: 5
      }
    })
      .then(response => {
        this.searchCompanyResults = response.data.data
      })
  }

  @Watch('searchCompanyResults')
  private watchSearchCompanyResults (): void {
    if (this.searchCompanyResults === null || this.searchCompanyResults.length === 0) {
      window.document.removeEventListener('click', this.searchCompanyClose)
    } else {
      window.document.addEventListener('click', this.searchCompanyClose)
    }
  }

  private searchCompanyClose (event: MouseEvent): void {
    const target = event.target as HTMLElement
    const parent = target.closest('.customer-name-form-part')
    if (!target.classList.contains('customer-name-form-part') && !parent) {
      this.searchCompanyResults = null
    }
  }

  private selectCompany (siret: KwixeoCustomers.SiretApiResult): void {
    this.searchCompanyResults = null

    let company_name: string
    if (siret.unite_legale?.denominationUniteLegale) {
      company_name = siret.unite_legale?.denominationUniteLegale
    } else if (siret.denominationUsuelleEtablissement) {
      company_name = siret.denominationUsuelleEtablissement
    } else {
      company_name = siret.unite_legale?.prenomUsuelUniteLegale + ' ' + siret.unite_legale?.nomUniteLegale
    }
    this.localCustomer.company_name = this.uppercaseFirstLetter(company_name) as string

    if (!this.localCustomer.address_invoice) {
      this.localCustomer.address_invoice = {}
    }
    const road1: (string|null)[] = [siret.numeroVoieEtablissement, siret.indiceRepetitionEtablissement, siret.typeVoieEtablissement, siret.libelleVoieEtablissement]
    this.localCustomer.address_invoice.road1 = this.uppercaseFirstLetter(road1.filter(road => road !== null).join(' '))
    this.localCustomer.address_invoice.road2 = this.uppercaseFirstLetter(siret.complementAdresseEtablissement)
    this.localCustomer.address_invoice.road3 = ''
    this.localCustomer.address_invoice.town = this.uppercaseFirstLetter(siret.libelleCommuneEtablissement)
    this.localCustomer.address_invoice.postal_code = this.uppercaseFirstLetter(siret.codePostalEtablissement)

    this.localCustomer.company_siret = siret.siret

    if (siret.unite_legale?.vatrateChecked) {
      this.localCustomer.company_vatrate = siret.unite_legale?.vatrateNumber
    } else if (this.$kwCustomers.sireneApiCheckVatrate !== false) {
      this.checkVatRateNumber(siret.unite_legale?.siren as string)
    }
  }

  private uppercaseFirstLetter (term: string|null): string|null {
    if (!term || !this.teamConfigs.fetch_customer_sirene_api_styles) {
      return term
    }
    return term.charAt(0).toUpperCase() + term.slice(1).toLowerCase()
  }

  private async checkVatRateNumber (siren: string): Promise<void> {
    await axios.get(this.$kwCustomers.sireneApiUrl + '/vatrate', {
        headers: {
          Authorization: 'Bearer ' + this.$kwCustomers.sireneApitoken
        },
        withCredentials: true,
        params: {
          siren
        }
      })
      .then(response => {
        this.localCustomer.company_vatrate = response.data
      })
      .catch(() => {
        //
      })
  }
}
